import type { DocumentVersion } from "@prismicio/editor-fields"
import { createRouteParams } from "@prismicio/editor-support/URL"
import type { Document } from "@prismicio/types-internal/lib/content"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useCallback, useContext } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { z } from "zod"

import { useRouteParams, useRouteState } from "../app/routing"
import type { DocumentVersionStatus } from "../app/services/core"
import {
  getAllReleasesQueryOptions,
  type MigrationRelease,
} from "../app/services/release"
import { useTeamSpaces } from "../app/services/role"
import { DocumentContext } from "./DocumentContext"

export function useExistingDocumentParams() {
  const { path, search } = useRouteParams({
    path: { documentId: z.string() },
    search: {
      // Document status
      s: z
        .enum(["unclassified", "published", "release", "archived"])
        .optional(),
      // Release id
      r: z.string().optional(),
      // Version id (required to differentiate archived docs)
      v: z.string().optional(),
    },
  })

  // While we use single letter query strings, we re-map these to nice to read Typescript code.
  return {
    documentId: path.documentId,
    releaseId: search.r,
    status: search.s,
    archivedVersionId: search.v,
  }
}

export function useNewDocumentParams() {
  const { search } = useRouteParams({
    search: {
      custom_type: z.string(),
      group_lang_id: z.string().optional(),
      locale: z.string(),
      r: z.string().optional(),
    },
  })

  return {
    customTypeId: search.custom_type,
    groupLangId: search.group_lang_id,
    locale: search.locale,
    releaseId: search.r,
  }
}

export function useDocumentParams() {
  const { search } = useRouteParams({
    search: {
      section: z.string().optional(),
      id: z.string().optional(),
    },
  })

  const { section, id } = search

  return {
    section,
    selectionId: id,
  }
}

interface GoToDocumentArgs {
  id: string
  releaseId?: string
  archivedVersionId?: string
  status: DocumentVersionStatus
  replace: boolean
  initialDraft?: Document
}

export function useGoToDocument() {
  const navigate = useNavigate()

  return useCallback(
    (args: GoToDocumentArgs) => {
      const {
        id,
        replace,
        initialDraft,
        status,
        releaseId,
        archivedVersionId,
      } = args

      const to = getDocumentTo({
        id,
        status,
        releaseId,
        archivedVersionId,
      })

      navigate(to, { replace, state: { initialDraft } })
    },
    [navigate],
  )
}

interface GoToNewDocumentArgs {
  customTypeId: string
  groupLangId?: string
  locale: string
  initialDraft?: Document
  tags?: string[]
  title?: string
  releaseId?: string
}

export function useGoToNewDocument() {
  const navigate = useNavigate()

  return useCallback(
    (args: GoToNewDocumentArgs) => {
      const {
        customTypeId,
        groupLangId,
        locale,
        initialDraft,
        tags,
        title,
        releaseId,
      } = args

      const routeParams = {
        custom_type: customTypeId,
        group_lang_id: groupLangId,
        r: releaseId,
        locale,
      }

      const newSearchParams = createRouteParams({ params: routeParams })

      const searchParams = new URLSearchParams(newSearchParams).toString()

      const to = `/pages/new?${searchParams}`

      navigate(to, {
        replace: false,
        state: { document: initialDraft, tags, title },
      })
    },
    [navigate],
  )
}

interface GetDocumentToArgs {
  id: string
  releaseId?: string
  archivedVersionId?: string
  status: DocumentVersionStatus
}

function getDocumentTo(args: GetDocumentToArgs) {
  const { id, status, releaseId, archivedVersionId } = args

  const routeParams = {
    s: status,
    r: releaseId,
    v: archivedVersionId,
  }

  const newSearchParams = createRouteParams({ params: routeParams })

  const searchParams = new URLSearchParams(newSearchParams)
  return `/pages/${id}?${searchParams.toString()}`
}

export function useGoToDocumentVersion() {
  const [_, setSearchParams] = useSearchParams()

  interface CallbackArgs {
    releaseId?: string
    status: DocumentVersionStatus
    preventScrollReset?: boolean
  }

  return useCallback(
    (params: CallbackArgs) => {
      const { releaseId, status, preventScrollReset } = params

      const routeParams = {
        s: status,
        r: releaseId,
      }
      const newSearchParams = createRouteParams({ params: routeParams })

      setSearchParams(
        (previousSearchParams) => {
          // Remove the current slice and version selection (if any) when switching versions.
          const {
            id: _documentId,
            r: _releaseId,
            v: _versionId,
            ...searchParams
          } = Object.fromEntries(previousSearchParams)

          return { ...searchParams, ...newSearchParams }
        },
        { replace: true, preventScrollReset },
      )
    },
    [setSearchParams],
  )
}

export function useGoToDocumentSelection() {
  const [_, setSearchParams] = useSearchParams()

  return useCallback(
    (selectionId: string | undefined) => {
      setSearchParams(
        (searchParams) => {
          const newParams = new URLSearchParams(searchParams)

          if (selectionId) {
            newParams.set("id", selectionId)
          } else {
            newParams.delete("id")
          }

          return newParams
        },
        { replace: true, preventScrollReset: true },
      )
    },
    [setSearchParams],
  )
}

/**
 * Go to the document list programmatically.
 * Think hard before using this, you probably can use an anchor with an href instead, which provides better UX.
 */
export function useGoToDocumentList() {
  const navigate = useNavigate()
  return useCallback(() => navigate("/"), [navigate])
}

interface UseRepositoryReleasesArgs {
  includeMigrationRelease?: boolean
}

export function useRepositoryReleases(args?: UseRepositoryReleasesArgs) {
  const { includeMigrationRelease = false } = args || {}

  const { data: releases } = useSuspenseQuery(getAllReleasesQueryOptions)

  return includeMigrationRelease
    ? releases
    : releases.filter((release) => !release.migration)
}

export function useRepositoryMigrationRelease(): MigrationRelease | undefined {
  const releases = useRepositoryReleases({ includeMigrationRelease: true })

  const migrationRelease = releases.find(
    (release): release is MigrationRelease => release.migration === true,
  )

  return migrationRelease
}

export function useDocumentListBackLink() {
  const { locale, version, customType } = useContext(DocumentContext) ?? {}

  const teamSpaces = useTeamSpaces()

  const { lastDocListHref } = useRouteState()

  // If we've been to the doc list before, use this info to return to the exact state we were in, including all filters.
  if (lastDocListHref) return lastDocListHref

  // We're in a route that doesn't know about a current Document version, that's fine: Go to the default doc list screen.
  if (!version) return "/"

  // Compute a half-decent link to go back to, based on the partial information we have.
  const { status } = version

  let pathname = "working"
  if (status === "release") pathname = "upcoming"
  if (status === "archived") pathname = "archived"

  const searchParams = new URLSearchParams()

  if (locale) {
    searchParams.append("language", locale)
  }

  if (customType) {
    const teamSpace = teamSpaces.find((teamSpace) =>
      teamSpace.customTypes.includes(customType.id),
    )
    if (teamSpace?.id) {
      searchParams.append("space", teamSpace.id)
    }
  }

  const query = searchParams.toString()

  return `/${pathname}${query ? `?${query}` : ""}`
}

//----------------------------------------------------------------------------------------
//  Old wroom history navigation below (delete when version history screen is implemented in Page Builder)
//----------------------------------------------------------------------------------------

type ReleaseDocumentVersion = Extract<DocumentVersion, { status: "release" }>

// Used to check whether the specified document version comes from a migration release
export function useIsVersionInMigrationRelease() {
  const migrationRelease = useRepositoryMigrationRelease()

  const migrationReleaseId = migrationRelease?.id

  return useCallback(
    (version: DocumentVersion): version is ReleaseDocumentVersion =>
      version.status === "release" && version.release_id == migrationReleaseId,
    [migrationReleaseId],
  )
}

export function useGoToOldRevisionHistory() {
  return useCallback(
    (args: {
      documentId: string
      version: DocumentVersion
      locale: string
    }) => {
      const { documentId, version, locale } = args

      const params = new URLSearchParams()
      params.set("b", versionToB(version))
      params.set("c", versionToC(version))

      if (!params.get("l")) params.set("l", locale)
      const r = versionToR(version)

      if (r) params.set("r", r)
      else params.delete("r")

      const paramsPath = `~${params.toString()}`

      const versionPath =
        version.status === "archived" ? `*${version.version_id}` : ""

      const historyPath = `${version.version_id}~${version.version_id}`

      const url = `/documents${paramsPath}/${documentId}${versionPath}/history/${historyPath}`

      window.location.assign(url)
    },
    [],
  )
}

const versionToB = (version: DocumentVersion) => {
  switch (version.status) {
    case "unclassified":
    case "published":
      return "working"
    case "archived":
      return "archived"
    case "release":
      return `upcoming:${version.release_id}`
  }
}

const versionToC = (version: DocumentVersion) =>
  `${version.status}${
    version.status === "release" ? `:${version.release_id}` : ""
  }`

const versionToR = (version: DocumentVersion) =>
  version.status === "release" ? version.release_id : null
